/**
 * 연결 규칙 — 캔버스에서 선을 **잇는 순간** 판정한다. DFD 검사 바(utils/dfdRules)는 다 그린 뒤의 전체 검사이고,
 * 이것은 한 번의 연결이 들어와도 되는지를 본다.
 *
 * 규칙은 두 부류다.
 *   금지(forbidden) — 절대 이으면 안 되는 것.
 *       1안 연결하지 않고 이유를 알린다
 *       2안 (고칠 길이 있는 것만) 사이에 알맞은 노드를 넣어 잇도록 유도한다 — 데이터→데이터면 데이터처리, 처리→처리면 내부데이터
 *   주의(caution) — 이어도 되지만 의도를 확인할 만한 것.
 *       1안 잇고 알림만 띄운다
 *       2안 잇기 전에 확인을 받는다
 * 사용자가 규칙마다 1안/2안을 고를 수 있다(설정 — flowRuleSettings). 고칠 길이 없는 금지 규칙(자기 자신·중복·순환)은 1안 고정.
 */
import type { Node, Edge } from '@xyflow/react';
import { dfdClass } from './dfdRules';
import { isErrorEdge } from './errorFlow';

export type RuleKind = 'forbidden' | 'caution';
export type RuleChoice = 'opt1' | 'opt2';

export interface FlowRule {
  id: string;
  kind: RuleKind;
  /** 고를 수 있는 안 — 하나뿐이면 고정 */
  options: RuleChoice[];
  defaultChoice: RuleChoice;
  /** 금지 규칙 2안에서 사이에 넣을 노드 유형 */
  insertType?: string;
}

export const FLOW_RULES: FlowRule[] = [
  { id: 'selfLoop', kind: 'forbidden', options: ['opt1'], defaultChoice: 'opt1' },
  { id: 'duplicate', kind: 'forbidden', options: ['opt1'], defaultChoice: 'opt1' },
  { id: 'cycle', kind: 'forbidden', options: ['opt1'], defaultChoice: 'opt1' },
  { id: 'dataToData', kind: 'forbidden', options: ['opt1', 'opt2'], defaultChoice: 'opt2', insertType: 'transformLoad' },
  { id: 'processToProcess', kind: 'forbidden', options: ['opt1', 'opt2'], defaultChoice: 'opt2', insertType: 'innerData' },
  { id: 'execWithData', kind: 'caution', options: ['opt1', 'opt2'], defaultChoice: 'opt1' },
  { id: 'dataFanIn', kind: 'caution', options: ['opt1', 'opt2'], defaultChoice: 'opt2' },
  { id: 'processInputs', kind: 'caution', options: ['opt1', 'opt2'], defaultChoice: 'opt2' },
];

export const ruleById = (id: string) => FLOW_RULES.find((r) => r.id === id);

export interface RuleHit {
  rule: FlowRule;
  /** 안내 문구에 넣을 값 */
  params: Record<string, string | number>;
}

export interface ConnectionVerdict {
  /** 걸린 금지 규칙(첫 번째) — 있으면 그대로는 잇지 않는다 */
  forbidden: RuleHit | null;
  /** 걸린 주의 규칙들 */
  cautions: RuleHit[];
}

const label = (n?: Node) => String((n?.data as any)?.label || n?.type || '?');

/** 처리 노드가 정상 입력을 몇 개까지 받는가 — 조인·데이터비교는 둘, 나머지는 하나 */
export const maxInputs = (type?: string) => (type === 'join' || type === 'compare' ? 2 : 1);

/** target 에서 출발해 기존 엣지를 따라 source 에 닿으면 이 연결이 순환을 만든다 */
function makesCycle(sourceId: string, targetId: string, edges: Edge[]): boolean {
  const seen = new Set<string>();
  const stack = [targetId];
  while (stack.length) {
    const id = stack.pop()!;
    if (id === sourceId) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    edges.forEach((e) => { if (e.source === id) stack.push(e.target); });
  }
  return false;
}

/**
 * 이 연결(source → target)을 판정한다. sourceHandle 이 예외 흐름(실패 시 가는 길)이면 데이터 교대 규칙은 보지 않는다
 * — 그 길은 값이 흐르는 길이 아니다(utils/errorFlow, DFD 검사와 같은 기준).
 */
export function evaluateConnection(
  conn: { source: string; target: string; sourceHandle?: string | null },
  nodes: Node[], edges: Edge[],
): ConnectionVerdict {
  const src = nodes.find((n) => n.id === conn.source);
  const tgt = nodes.find((n) => n.id === conn.target);
  const p = { from: label(src), to: label(tgt) };
  const hit = (id: string, params: Record<string, string | number> = p): RuleHit => ({ rule: ruleById(id)!, params });
  const v: ConnectionVerdict = { forbidden: null, cautions: [] };
  if (!src || !tgt) return v;

  if (conn.source === conn.target) { v.forbidden = hit('selfLoop'); return v; }
  if (edges.some((e) => e.source === conn.source && e.target === conn.target && (e.sourceHandle || null) === (conn.sourceHandle || null))) {
    v.forbidden = hit('duplicate'); return v;
  }
  if (makesCycle(conn.source, conn.target, edges)) { v.forbidden = hit('cycle'); return v; }

  const errorFlow = isErrorEdge({ sourceHandle: conn.sourceHandle } as Edge);
  if (errorFlow) return v;

  const sc = dfdClass(src.type);
  const tc = dfdClass(tgt.type);
  if (sc === 'data' && tc === 'data') { v.forbidden = hit('dataToData'); return v; }
  if (sc === 'process' && tc === 'process') { v.forbidden = hit('processToProcess'); return v; }

  if ((sc === 'exec' && tc === 'data') || (sc === 'data' && tc === 'exec')) v.cautions.push(hit('execWithData'));
  const normalIn = edges.filter((e) => e.target === tgt.id && !isErrorEdge(e));
  if (tc === 'data' && normalIn.some((e) => dfdClass(nodes.find((n) => n.id === e.source)?.type) === 'process')
      && sc === 'process') {
    v.cautions.push(hit('dataFanIn', { ...p, n: normalIn.length + 1 }));
  }
  if (tc === 'process' && normalIn.length >= maxInputs(tgt.type)) {
    v.cautions.push(hit('processInputs', { ...p, n: normalIn.length + 1, max: maxInputs(tgt.type) }));
  }
  return v;
}
